import React from "react";
import { Link, useParams } from "react-router-dom";
import { useGetTournamentDetails } from "../apiCalls/tournamentApiCalls";
import {
  Container,
  Card,
  Badge,
  Button,
  CountryLabel,
  STATUS_SOLID,
} from "../components/ui";
import CourtArt from "../components/ui/CourtArt";
import {
  CalendarIcon,
  PinIcon,
  TrophyIcon,
  BuildingIcon,
  GlobeIcon,
  ArrowLeftIcon,
  UsersIcon,
} from "../components/ui/icons";
import { formatDateRange, formatNumber } from "../utils";
import normalDate from "../utils";
import Loader from "../components/Loader";
import ErrorState from "../components/ErrorState";

const STATUS_LABEL = {
  upcoming: "Upcoming",
  ongoing: "Ongoing",
  completed: "Completed",
};

const DetailRow = ({ icon, label, children }) => (
  <div className="flex items-start justify-between gap-4 border-b border-border py-3 last:border-0">
    <span className="flex items-center gap-2 text-sm text-muted">
      {icon}
      {label}
    </span>
    <span className="text-right text-sm font-medium text-navy">{children}</span>
  </div>
);

const TournamentDetails = () => {
  const { tournamentId } = useParams();
  const { data, isLoading, isError } = useGetTournamentDetails(tournamentId);

  if (isLoading) return <Loader label="Loading tournament…" />;
  if (isError) return <ErrorState message="We couldn't load this tournament." />;

  const tournament = data?.data;
  if (!tournament) return <ErrorState message="Tournament not found." />;

  const status = tournament.status || "upcoming";
  const pointsTable = tournament.pointsTable;

  return (
    <Container className="py-8 md:py-10">
      <Link
        to="/searchTournaments"
        className="mb-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary-hover"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        Back to Tournaments
      </Link>

      <div className="grid gap-6 lg:grid-cols-[1fr_22rem]">
        <div className="min-w-0">
          {/* ------------------------------ Hero ------------------------------ */}
          <Card padded={false} className="overflow-hidden">
            <div className="grid md:grid-cols-2">
              <div className="relative aspect-video bg-bg md:aspect-auto md:min-h-[18rem]">
                {tournament.imgUrl ? (
                  <img
                    src={tournament.imgUrl}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <CourtArt seed={tournament.name} label={tournament.city} />
                )}
                <span
                  className={`absolute left-4 top-4 inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold shadow-sm ${STATUS_SOLID[status]}`}
                >
                  {STATUS_LABEL[status]}
                </span>
              </div>

              <div className="p-6">
                <div className="flex items-start justify-between gap-3">
                  <h1 className="text-2xl font-bold tracking-tight text-navy md:text-3xl">
                    {tournament.name}
                  </h1>
                  {tournament.country && (
                    <CountryLabel code={tournament.country} variant="flag" />
                  )}
                </div>

                {tournament.category && (
                  <Badge tone="primary" className="mt-3">
                    {tournament.category}
                  </Badge>
                )}

                <dl className="mt-5 space-y-2.5 text-sm">
                  <div className="flex items-center gap-2 text-navy">
                    <CalendarIcon className="h-4 w-4 text-muted" />
                    {formatDateRange(tournament.startDate, tournament.endDate)}
                  </div>
                  {(tournament.city || tournament.location) && (
                    <div className="flex items-center gap-2 text-navy">
                      <PinIcon className="h-4 w-4 text-muted" />
                      {tournament.city || tournament.location}
                    </div>
                  )}
                  {tournament.venue && (
                    <div className="flex items-center gap-2 text-navy">
                      <BuildingIcon className="h-4 w-4 text-muted" />
                      {tournament.venue}
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-navy">
                    <TrophyIcon className="h-4 w-4 text-muted" />
                    {formatNumber(tournament.rankingPoints)} Ranking Points
                  </div>
                </dl>

                <div className="mt-6">
                  <Button to="/register">Get Ranked</Button>
                </div>
              </div>
            </div>
          </Card>

          {/* ---------------------------- Overview ---------------------------- */}
          <Card className="mt-6">
            <h2 className="text-lg font-semibold text-navy">
              About the Tournament
            </h2>
            {tournament.description && (
              <p className="mt-3 leading-relaxed text-muted">
                {tournament.description}
              </p>
            )}

            <div className="mt-6 grid gap-5 border-t border-border pt-6 sm:grid-cols-2 lg:grid-cols-4">
              {[
                {
                  icon: <TrophyIcon />,
                  label: "Level",
                  value: tournament.category,
                },
                {
                  icon: <GlobeIcon />,
                  label: "Surface",
                  value: tournament.surface,
                },
                {
                  icon: <UsersIcon />,
                  label: "Draw Size",
                  value: tournament.drawSize
                    ? `${tournament.drawSize} Pairs`
                    : null,
                },
                {
                  icon: <TrophyIcon />,
                  label: "Winner Points",
                  value: tournament.rankingPoints
                    ? formatNumber(tournament.rankingPoints)
                    : null,
                },
              ]
                .filter((item) => item.value)
                .map((item) => (
                  <div key={item.label} className="flex items-center gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border text-primary">
                      {item.icon}
                    </span>
                    <div className="min-w-0">
                      <div className="text-xs font-medium text-muted">
                        {item.label}
                      </div>
                      <div className="truncate text-sm font-semibold text-navy">
                        {item.value}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </Card>
        </div>

        {/* ----------------------------- Sidebar ----------------------------- */}
        <aside className="space-y-6">
          <Card>
            <h2 className="mb-2 font-semibold text-navy">Tournament Details</h2>
            <DetailRow icon={<CalendarIcon className="h-4 w-4" />} label="Start Date">
              {normalDate(tournament.startDate)}
            </DetailRow>
            <DetailRow icon={<CalendarIcon className="h-4 w-4" />} label="End Date">
              {normalDate(tournament.endDate)}
            </DetailRow>
            {tournament.city && (
              <DetailRow icon={<PinIcon className="h-4 w-4" />} label="Location">
                {tournament.city}
              </DetailRow>
            )}
            {tournament.venue && (
              <DetailRow
                icon={<BuildingIcon className="h-4 w-4" />}
                label="Venue"
              >
                {tournament.venue}
              </DetailRow>
            )}
            {tournament.country && (
              <DetailRow icon={<GlobeIcon className="h-4 w-4" />} label="Country">
                <CountryLabel code={tournament.country} />
              </DetailRow>
            )}
            {tournament.category && (
              <DetailRow
                icon={<TrophyIcon className="h-4 w-4" />}
                label="Category"
              >
                {tournament.category}
              </DetailRow>
            )}
            {tournament.website && (
              <DetailRow icon={<GlobeIcon className="h-4 w-4" />} label="Website">
                <a
                  href={tournament.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Visit site ↗
                </a>
              </DetailRow>
            )}
          </Card>

          {pointsTable && (
            <Card padded={false}>
              <h2 className="border-b border-border px-5 py-4 font-semibold text-navy">
                Ranking Points
              </h2>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-semibold uppercase tracking-wide text-muted">
                    <th className="px-5 py-2.5">Round</th>
                    <th className="px-5 py-2.5 text-right">Points</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {Object.entries(pointsTable).map(([round, points]) => (
                    <tr key={round}>
                      <td className="px-5 py-2.5 text-navy">{round}</td>
                      <td className="tabular px-5 py-2.5 text-right font-semibold text-navy">
                        {formatNumber(points)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </aside>
      </div>
    </Container>
  );
};

export default TournamentDetails;
